/** Mail display helpers: tab / folder icons and tones, Gmail's short list dates and attachment sizes. */
import type { MailTabDef } from "@bundle/contracts";
import type { MailView } from "@bundle/core";
import type { ChipTone } from "../primitives/Chip.tsx";
import { iconGlyphs, type IconName } from "@bundle/tokens";

/** A server tab's icon: its glyph when this build has it, else a tag (a newer server may name newer glyphs). */
export function mailTabIcon(def: Pick<MailTabDef, "icon"> | undefined): IconName {
  return def !== undefined && Object.hasOwn(iconGlyphs, def.icon) ? (def.icon as IconName) : "tag";
}

export const MAIL_FOLDER_ICONS: Record<MailView, IconName> = {
  inbox: "inbox", starred: "star", snoozed: "clock", scheduled: "hourglass", drafts: "edit", sent: "send", archive: "archive", trash: "trash", spam: "ban",
};
/** The rail's order (Gmail): inbox, starred, snoozed, sent, drafts, scheduled, then archive, spam, trash. */
export const MAIL_VIEWS: MailView[] = ["inbox", "starred", "snoozed", "sent", "drafts", "scheduled", "archive", "spam", "trash"];

const LABEL_COLORS = ["gray", "brown", "orange", "yellow", "green", "blue", "purple", "pink", "red"] as const;
export const MAIL_LABEL_COLORS: readonly string[] = LABEL_COLORS;
/** A label's chip tone (its colour as a tag colour; unknown ones are gray). */
export function labelTone(color: string): ChipTone {
  return (LABEL_COLORS as readonly string[]).includes(color) ? (`tag.${color}` as ChipTone) : "tag.gray";
}

/** A server tab's tone: "accent", or its colour as a tag colour (unknown ones are gray). */
export function mailTabTone(def: Pick<MailTabDef, "color"> | undefined): ChipTone {
  return def?.color === "accent" ? "accent" : labelTone(def?.color ?? "gray");
}

/** Gmail's list dates: the time today, "Oct 22" this year, "10/22/25" before. */
export function mailShortDate(ts: number, locale: string, now: number = Date.now()): string {
  const d = new Date(ts);
  const n = new Date(now);
  if (d.toDateString() === n.toDateString()) return d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  if (d.getFullYear() === n.getFullYear()) return d.toLocaleDateString(locale, { month: "short", day: "numeric" });
  return d.toLocaleDateString(locale, { year: "2-digit", month: "numeric", day: "numeric" });
}

/** An attachment's size ("12 kB"). */
export function mailFileSize(bytes: number, locale: string): string {
  const units = ["byte", "kilobyte", "megabyte", "gigabyte"] as const;
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return new Intl.NumberFormat(locale, { style: "unit", unit: units[i], unitDisplay: "short", maximumFractionDigits: i === 0 ? 0 : 1 }).format(v);
}
